/**
 * Plot Grid Generate cost band (#1174). Planning math only — labeled as
 * an estimate, never a bill and never a reason to refuse.
 *
 * Stands alone from Long Plot. Rates come from the OpenRouter catalog
 * (same join as Create Outline). Input size comes from assess
 * (`estimated_input_tokens`, or `prompt_chars` / 4). Output budgets match
 * generate_service after #1171:
 *   fills spine: 16000 per call, up to 4 calls (1 + 3 same-pass Continues)
 *   otherwise: 10000, one call (beta-bar 12: two to four scenes a sequence)
 *
 * Typical is one call of about 4000 output tokens: a 25–40 scene outline runs
 * about 100 tokens a scene (run 46: 18 scenes in 1,962 tokens, $0.026 against
 * a low figure of $0.17; staging 30 days: most Generates under $0.05). Worst
 * prices every call at the ceiling, Continues included.
 *
 * fills:    typical = 1×(I×Rp + 4000×Rc), worst = 4×(I×Rp + 16000×Rc)
 * non-fills: typical = 1×(I×Rp + 4000×Rc), worst = 1×(I×Rp + 10000×Rc)
 */

import {
  formatEstimateAbout,
  formatEstimateRange,
  formatEstimateUsd,
  pricingFromCatalog,
} from './estimateOutlineCost';

export const STORY_BOARD_GENERATION_KEY = 'story_board_generation';

/** Backend DEFAULT_MODELS['story_board_generation'] when Settings has no override. */
export const STORY_BOARD_GENERATION_DEFAULT_MODEL = 'anthropic/claude-sonnet-5';

export const CHARS_PER_TOKEN = 4;

export const FILLS_OUTPUT_BUDGET = 16000;
export const FILLS_WORST_CALLS = 4;
export const FILLS_TYPICAL_CALLS = 1;
export const FILLS_TYPICAL_OUTPUT = 4000;

export const NON_FILLS_OUTPUT_BUDGET = 10000;
export const NON_FILLS_WORST_CALLS = 1;
export const NON_FILLS_TYPICAL_CALLS = 1;
export const NON_FILLS_TYPICAL_OUTPUT = 4000;

export const CANT_ESTIMATE_RATES_LINE =
  "Can't estimate — OpenRouter rates didn't load. You can still confirm.";

export const CANT_ESTIMATE_SIZE_LINE =
  "Can't estimate — prompt size didn't load. You can still confirm.";

export const ESTIMATE_NOT_EXACT = 'Not an exact cost.';

export function outputPlan(fillsSpine) {
  if (fillsSpine) {
    return {
      typicalCalls: FILLS_TYPICAL_CALLS,
      typicalOutput: FILLS_TYPICAL_OUTPUT,
      worstCalls: FILLS_WORST_CALLS,
      worstOutput: FILLS_OUTPUT_BUDGET,
    };
  }
  return {
    typicalCalls: NON_FILLS_TYPICAL_CALLS,
    typicalOutput: NON_FILLS_TYPICAL_OUTPUT,
    worstCalls: NON_FILLS_WORST_CALLS,
    worstOutput: NON_FILLS_OUTPUT_BUDGET,
  };
}

export function resolveInputTokens({ estimatedInputTokens, promptChars } = {}) {
  if (estimatedInputTokens != null && estimatedInputTokens !== '') {
    const tokens = Number(estimatedInputTokens);
    if (Number.isFinite(tokens) && tokens >= 0) return Math.floor(tokens);
  }
  if (promptChars != null && promptChars !== '') {
    const chars = Number(promptChars);
    if (Number.isFinite(chars) && chars >= 0) return Math.floor(chars / CHARS_PER_TOKEN);
  }
  return null;
}

export function resolveStoryBoardModelId(modelPreferences) {
  const saved = modelPreferences?.[STORY_BOARD_GENERATION_KEY];
  const text = typeof saved === 'string' ? saved.trim() : '';
  return text || STORY_BOARD_GENERATION_DEFAULT_MODEL;
}

/**
 * @returns {{typical: number, worst: number, inputTokens: number}|null}
 */
export function estimatePlotTrackGenerateCostUsd(modelPricing, size = {}) {
  const prompt = Number(modelPricing?.prompt);
  const completion = Number(modelPricing?.completion);
  if (!Number.isFinite(prompt) || !Number.isFinite(completion)) return null;
  const inputTokens = resolveInputTokens(size);
  if (inputTokens == null) return null;
  const plan = outputPlan(size.fillsSpine === true);
  const call = (calls, output) => calls * (inputTokens * prompt + output * completion);
  return {
    typical: call(plan.typicalCalls, plan.typicalOutput),
    worst: call(plan.worstCalls, plan.worstOutput),
    inputTokens,
    ...plan,
  };
}

export function formatGenerateEstimateLine(band) {
  if (!band || !Number.isFinite(band.typical) || !Number.isFinite(band.worst)) return null;
  const amount = formatEstimateRange(band.typical, band.worst);
  if (!amount) return null;
  return `Estimate: ${formatEstimateAbout(amount)} for this run. ${ESTIMATE_NOT_EXACT}`;
}

export { formatEstimateUsd, pricingFromCatalog };
